import React, { Children, useMemo } from 'react'
import cx from 'classnames'
import { bool, element, func, node, number, oneOfType, string } from 'prop-types'

import { placementPropType } from '../tooltip/tooltip-prop-type'
import { WidthObservedElement } from './width-observed-element'
import { MoreButton } from './more-button'
import { dropdownPlacementTypesPropType } from './constants'

import styles from './button-bar-content.module.css'

const propTypes = {
    additionalContent: oneOfType([node, element]),
    children: node,
    className: string,
    dropdownPlacement: dropdownPlacementTypesPropType,
    hideIfEmpty: bool,
    moreButtonTooltip: string,
    moreButtonTooltipPlacement: placementPropType,
    noBackground: bool,
    noPadding: bool,
    numberOfVisibleButtons: number,
    onAdditionalContentWidthChanged: func,
    onAvailableWidthChanged: func,
    onButtonWidthChanged: func,
    onMoreButtonWidthChanged: func,
    testClassNamePostfix: string
}

const defaultProps = {
    onAvailableWidthChanged: () => {},
    onButtonWidthChanged: () => {},
    onMoreButtonWidthChanged: () => {}
}

export function ButtonBarContent({
    additionalContent,
    children,
    className,
    dropdownPlacement,
    hideIfEmpty,
    moreButtonTooltip,
    moreButtonTooltipPlacement,
    noBackground,
    noPadding,
    numberOfVisibleButtons,
    onAdditionalContentWidthChanged,
    onAvailableWidthChanged,
    onButtonWidthChanged,
    onMoreButtonWidthChanged,
    testClassNamePostfix
}) {
    const buttons = useMemo(() => Children.toArray(children), [children])
    const lastVisibleButtonIndex = numberOfVisibleButtons - 1
    const hiddenButtons = useMemo(() => buttons.slice(lastVisibleButtonIndex + 1), [buttons, lastVisibleButtonIndex])

    const moreButton = useMemo(
        () =>
            hiddenButtons.length > 0 && (
                <MoreButton
                    key="more-button"
                    {...{
                        dropdownPlacement,
                        moreButtonTooltip,
                        moreButtonTooltipPlacement,
                        onWidthChanged: onMoreButtonWidthChanged
                    }}
                >
                    {hiddenButtons}
                </MoreButton>
            ),
        [dropdownPlacement, hiddenButtons, moreButtonTooltip, moreButtonTooltipPlacement, onMoreButtonWidthChanged]
    )

    const renderButtons = useMemo(() => {
        const visibleButtons = buttons.map((button, index) => (
            <WidthObservedElement
                key={`button-${index}`}
                onWidthChanged={width => onButtonWidthChanged(index, width)}
                visible={index <= lastVisibleButtonIndex}
            >
                {button}
            </WidthObservedElement>
        ))

        if (moreButton) {
            visibleButtons.splice(lastVisibleButtonIndex + 1, 0, moreButton)
        }
        return visibleButtons
    }, [buttons, lastVisibleButtonIndex, moreButton, onButtonWidthChanged])

    const t3selClassName = ['t3sel-button-bar', testClassNamePostfix].filter(Boolean).join('-')
    const buttonBarClassName = cx(styles['button-bar'], className, t3selClassName, {
        [styles['hidden']]: hideIfEmpty && !Children.toArray(children).length,
        [styles['no-padding']]: noPadding,
        [styles['no-background']]: noBackground
    })
    return (
        <nav className={buttonBarClassName}>
            <WidthObservedElement className={styles['content-wrapper']} onWidthChanged={onAvailableWidthChanged}>
                {additionalContent && (
                    <WidthObservedElement
                        className="t3sel-button-bar--additional-content"
                        onWidthChanged={onAdditionalContentWidthChanged}
                    >
                        {additionalContent}
                    </WidthObservedElement>
                )}
                {renderButtons}
            </WidthObservedElement>
        </nav>
    )
}

ButtonBarContent.propTypes = propTypes
ButtonBarContent.defaultProps = defaultProps
